// Catalog read service. All projections happen here — feature code receives
// Product / Category / Brand domain objects, never the raw items.Items shape.
import { apiClient, endpoints, unwrap } from '@core/api';
import { useMenuStore } from '@core/store/menu';
import { getPlatformSlug } from '@core/store/storeContextStore';
import { toCategory, toProduct, sortByDisplayOrder } from './mappers/item.js';

function asList(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];

  const candidates = [
    data.items,
    data.products,
    data.records,
    data.results,
    data.rows,
    data.list,
    data.data,
  ];

  return candidates.find(Array.isArray) ?? [];
}

function asCategoryList(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];

  const candidates = [
    data.categories,
    data.items,
    data.records,
    data.results,
    data.rows,
    data.list,
    data.data,
  ];

  return candidates.find(Array.isArray) ?? [];
}

async function hydrateModifierGroups(rawProduct) {
  const modifierGroupIds = Array.isArray(rawProduct?.modifier_group_ids)
    ? rawProduct.modifier_group_ids.filter(Boolean)
    : [];
  if (modifierGroupIds.length === 0) return rawProduct;

  const existingGroups = Array.isArray(rawProduct.modifier_groups)
    ? rawProduct.modifier_groups
    : Array.isArray(rawProduct.modifier_group_details)
      ? rawProduct.modifier_group_details
      : [];

  if (existingGroups.length === 0) return rawProduct;

  return {
    ...rawProduct,
    modifier_group_details: existingGroups,
    modifier_groups: existingGroups,
  };
}

/**
 * @param {{ locationId?: string, channelId?: string, categoryId?: string, page?: number, limit?: number, search?: string, sort?: string, priceField?: string, signal?: AbortSignal }} input
 * @returns {Promise<import('@core/types').Page<import('@core/types').Product>>}
 */
export async function listProducts({
  accountId,
  locationId,
  channelId,
  categoryId,
  page = 1,
  limit = 20,
  search,
  sort,
  priceField,
  foodType,
  signal,
} = {}) {
  let resolvedFoodType = foodType;
  if (foodType === 'veg' || foodType === 'nonveg') {
    let { vegMasterId, nonVegMasterId, fetchMasters } = useMenuStore.getState();
    if (!vegMasterId || !nonVegMasterId) {
      await fetchMasters();
      const updated = useMenuStore.getState();
      vegMasterId = updated.vegMasterId;
      nonVegMasterId = updated.nonVegMasterId;
    }
    if (foodType === 'veg') {
      resolvedFoodType = vegMasterId || '';
    } else {
      resolvedFoodType = nonVegMasterId || '';
    }
  }

  const raw = await apiClient.get(endpoints.catalog.items, {
    signal,
    query: {
      account_id: accountId,
      location_id: locationId,
      category_id: categoryId,
      status: 'PUBLISHED',
      is_active: true,
      platform_slug: getPlatformSlug(),
      online_availability: true,
      page,
      limit,
      search,
      sort,
      food_type: resolvedFoodType,
    },
  });
  const { data, meta } = unwrap(raw);
  const list = asList(data);
  let products = list.map((item) => toProduct(item, { priceField, locationId, channelId }));
  products = sortByDisplayOrder(products);
  return {
    data: products,
    meta,
  };
}

/**
 * Fetch products for multiple category IDs simultaneously.
 * Makes parallel API calls for each ID and merges + deduplicates the results.
 * Falls back to a single call when categoryIds is empty/undefined.
 *
 * @param {{ locationId?: string, categoryIds?: string[], page?: number, limit?: number, search?: string, sort?: string, priceField?: string, foodType?: string, signal?: AbortSignal }} input
 * @returns {Promise<import('@core/types').Page<import('@core/types').Product>>}
 */
export async function listProductsByCategories({
  accountId,
  locationId,
  channelId,
  categoryIds,
  page = 1,
  limit = 20,
  search,
  sort,
  priceField,
  foodType,
  signal,
} = {}) {
  // No category filter — return all
  if (!categoryIds || categoryIds.length === 0) {
    return listProducts({
      accountId,
      locationId,
      channelId,
      page,
      limit,
      search,
      sort,
      priceField,
      foodType,
      signal,
    });
  }

  // Single category — use the simple path
  if (categoryIds.length === 1) {
    return listProducts({
      accountId,
      locationId,
      channelId,
      categoryId: categoryIds[0],
      page,
      limit,
      search,
      sort,
      priceField,
      foodType,
      signal,
    });
  }

  // Multiple categories — fetch all in parallel, then merge & deduplicate
  const results = await Promise.all(
    categoryIds.map((id) =>
      listProducts({
        accountId,
        locationId,
        channelId,
        categoryId: id,
        limit: 200,
        search,
        sort,
        priceField,
        foodType,
        signal,
      }),
    ),
  );

  const seen = new Set();
  const merged = [];
  for (const result of results) {
    for (const item of result.data) {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        merged.push(item);
      }
    }
  }

  const sortedMerged = sortByDisplayOrder(merged);

  // Respect the requested page/limit on the merged result
  const start = (page - 1) * limit;
  const pageItems = sortedMerged.slice(start, start + limit);

  return {
    data: pageItems,
    meta: {
      totalCount: merged.length,
      page,
      perPage: limit,
      totalPages: Math.ceil(merged.length / limit),
    },
  };
}

/**
 * @param {string} id
 * @param {{ priceField?: string, locationId?: string, channelId?: string }} [opts]
 * @returns {Promise<import('@core/types').Product>}
 */
export async function getProduct(id, opts = {}) {
  const raw = await apiClient.get(endpoints.catalog.itemPlatformView(id), {
    signal: opts.signal,
    query: {
      account_id: opts.accountId,
      location_id: opts.locationId,
      platform_slug: getPlatformSlug(),
    },
  });
  const { data } = unwrap(raw);
  const hydrated = await hydrateModifierGroups(data ?? raw);
  return toProduct(hydrated, opts);
}

// Categories sort by `display_order` ascending at every level (top, sub,
// sub-sub). Missing / non-numeric orders go last; ties keep the API order.
function categoryOrder(raw) {
  const value = Number(raw?.display_order ?? raw?.displayOrder);
  return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

function sortRawCategories(list) {
  return list
    .map((raw, index) => ({ raw, index }))
    .sort((a, b) => categoryOrder(a.raw) - categoryOrder(b.raw) || a.index - b.index)
    .map(({ raw }) => raw);
}

function filterRawCategoryTree(raw) {
  if (!raw) return null;
  const rawSubs = raw.subCategories ?? raw.sub_categories;
  const subCategories = sortRawCategories(
    (Array.isArray(rawSubs) ? rawSubs : []).map(filterRawCategoryTree).filter(Boolean),
  );
  const rawSubSubs = raw.subSubCategories ?? raw.sub_sub_categories;
  const subSubCategories = sortRawCategories(
    (Array.isArray(rawSubSubs) ? rawSubSubs : []).map(filterRawCategoryTree).filter(Boolean),
  );
  const hasSelfItems =
    Number(raw.items_count ?? raw.itemsCount ?? 0) > 0 ||
    subCategories.length > 0 ||
    subSubCategories.length > 0;
  if (hasSelfItems) {
    return {
      ...raw,
      subCategories,
      subSubCategories,
    };
  }
  return null;
}

/**
 * @param {{ locationId?: string, signal?: AbortSignal }} [input]
 * @returns {Promise<import('@core/types').Category[]>}
 */
export async function listCategories({
  accountId,
  locationId,
  page = 1,
  limit = 100,
  signal,
} = {}) {
  const raw = await apiClient.get(endpoints.catalog.categories, {
    signal,
    query: {
      page,
      limit,
      items_count_need: true,
      account_id: accountId,
      location_id: locationId,
      platform_slug: getPlatformSlug(),
    },
  });
  const { data } = unwrap(raw);
  const list = asCategoryList(data);
  const filteredList = sortRawCategories(list.map(filterRawCategoryTree).filter(Boolean));
  return dedupeCategories(filteredList.flatMap((category) => flattenCategory(category)));
}

/**
 * @param {{ page?: number, limit?: number, locationId?: string, signal?: AbortSignal }} input
 * @returns {Promise<import('@core/types').Page<import('@core/types').Category>>}
 */
export async function listCategoryPage({
  page = 1,
  limit = 100,
  accountId,
  locationId,
  signal,
} = {}) {
  const raw = await apiClient.get(endpoints.catalog.categories, {
    signal,
    query: {
      page,
      limit,
      items_count_need: true,
      account_id: accountId,
      location_id: locationId,
      platform_slug: getPlatformSlug(),
    },
  });
  const { data, meta } = unwrap(raw);
  const list = asCategoryList(data);
  const filteredList = sortRawCategories(list.map(filterRawCategoryTree).filter(Boolean));
  const categories = filteredList.map(toCategory).filter((category) => Boolean(category.id));

  return {
    data: categories,
    meta,
  };
}

function flattenCategory(raw) {
  const category = toCategory(raw);
  const children = [
    ...(Array.isArray(raw.subCategories) ? raw.subCategories : []),
    ...(Array.isArray(raw.subSubCategories) ? raw.subSubCategories : []),
  ];
  const subcategories = children.flatMap((child) => flattenCategory(child));
  return [category, ...subcategories];
}

function dedupeCategories(categories) {
  const seen = new Set();
  return categories.filter((category) => {
    if (!category.id || seen.has(category.id)) return false;
    seen.add(category.id);
    return true;
  });
}
